import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, UserSearch } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';

interface Candidate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job_title: string;
  status: string;
  source: string;
  created_at: string;
  rating?: number;
}

export function CandidatesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const { data, loading } = useApi<{ candidates: Candidate[] }>(`candidates?search=${search}&status=${status}`, [search, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Search candidates..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-2 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
          <option value="">All Stages</option>
          <option value="new">New</option>
          <option value="screening">Screening</option>
          <option value="interview">Interview</option>
          <option value="assessment">Assessment</option>
          <option value="offer">Offer</option>
          <option value="hired">Hired</option>
          <option value="rejected">Rejected</option>
        </select>
        <button onClick={() => navigate('/candidates/new')} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
          <Plus className="w-4 h-4" /> Add Candidate
        </button>
      </div>
      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'name', header: 'Name', render: (r: Candidate) => <span className="font-medium">{r.first_name} {r.last_name}</span> },
            { key: 'email', header: 'Email' },
            { key: 'job_title', header: 'Position' },
            { key: 'source', header: 'Source', render: (r: Candidate) => <span className="capitalize">{r.source}</span> },
            { key: 'status', header: 'Stage', render: (r: Candidate) => <StatusBadge status={r.status} /> },
            { key: 'created_at', header: 'Applied', render: (r: Candidate) => r.created_at?.split('T')[0] ?? '' },
          ]}
          data={data?.candidates ?? []}
          onRowClick={(row) => navigate(`/candidates/${row.id}`)}
          emptyNode={
            <EmptyState
              icon={<UserSearch className="w-12 h-12" />}
              title="No candidates yet"
              description="Add your first candidate to start tracking applicants."
              action={
                <button onClick={() => navigate('/candidates/new')} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
                  <Plus className="w-4 h-4" /> Add Candidate
                </button>
              }
            />
          }
        />
      )}
    </div>
  );
}
